import { Input } from 'api/grpc/resources';
import { either, taskEither } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import { useAsync } from 'react-use';
import { ReactLeft, UI_STATES } from './uiStates/uiStates';
import { getDataSecurily, isUrlSecured } from './inputs';

export const fetchTextFileTE = (url: string): taskEither.TaskEither<ReactLeft, string> =>
  pipe(
    taskEither.tryCatch(
      () => (isUrlSecured(url) ? getDataSecurily(url) : fetch(url)),
      (error) => ({ type: UI_STATES.error, props: { reason: (error as Error).message } }),
    ),
    // encode failure of response.text() promise in a Left
    taskEither.chain((response) => {
      return taskEither.tryCatch(
        () => response.text(),
        (error): ReactLeft => ({ type: UI_STATES.invalid, props: { reason: (error as Error).message } }),
      );
    }),
  );

/**
 * Hook that returns text of the input
 * @param input
 * @returns
 */
export function useInputText(input: CF.API.Inputs.TextInput): either.Either<ReactLeft, string> {
  const { value = either.left({ type: UI_STATES.loading }) } = useAsync(async () => {
    if ('raw' in input.data.text && input.data.text.raw) {
      return either.right(input.data.text.raw);
    }
    if ('url' in input.data.text && input.data.text.url) {
      const textContentE = await fetchTextFileTE(input.data.text.url)();
      return textContentE;
    }
    return either.left({ type: UI_STATES.invalid });
  }, [input.id]);

  return value;
}

/**
 * Hook that returns text of the input
 * @param input
 * @returns
 */
export function useGRPCInputText(input: Input): either.Either<ReactLeft, string> {
  const { value = either.left({ type: UI_STATES.loading }) } = useAsync(async () => {
    const text = input.getData()?.getText();
    if (!text) {
      return either.left({ type: UI_STATES.invalid });
    }
    if (text.getRaw()) {
      return either.right(text.getRaw());
    }
    if (text.getUrl()) {
      const textContentE = await fetchTextFileTE(text.getUrl())();
      return textContentE;
    }
    return either.left({ type: UI_STATES.invalid });
  }, [input.getId()]);

  return value;
}
